import React from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';

import { useWidgetRefresh } from '../../contexts/WidgetRefreshContext';
import { colors, spacing } from '../../core/theme/tokens';
import { GlassButton } from './GlassButton';

type RefreshButtonProps = {
  onPress?: () => void;
  refreshing?: boolean;
  /** Compact ↻ control for widget headers. Page headers use the labeled button. */
  compact?: boolean;
  accessibilityLabel?: string;
};

/**
 * Ghost refresh control. Uses WidgetRefreshScope when onPress is omitted.
 * Rate limits live on the server; this only disables while a refresh is in flight.
 */
export function RefreshButton({
  onPress,
  refreshing,
  compact = false,
  accessibilityLabel = 'Refresh',
}: RefreshButtonProps) {
  const scoped = useWidgetRefresh();
  const handlePress = onPress ?? scoped?.refresh;
  const isRefreshing = refreshing ?? scoped?.refreshing ?? false;

  if (!handlePress) return null;

  const a11y = isRefreshing ? 'Refreshing' : accessibilityLabel;

  if (compact) {
    return (
      <GlassButton
        variant="ghost"
        size="sm"
        onPress={handlePress}
        disabled={isRefreshing}
        accessibilityLabel={a11y}
      >
        {isRefreshing ? (
          <View style={styles.compactBusy}>
            <ActivityIndicator color={colors.accent} size="small" />
          </View>
        ) : (
          <Text style={styles.compactGlyph}>↻</Text>
        )}
      </GlassButton>
    );
  }

  return (
    <GlassButton
      variant="secondary"
      size="sm"
      onPress={handlePress}
      disabled={isRefreshing}
      accessibilityLabel={a11y}
    >
      {isRefreshing ? 'Refreshing…' : 'Refresh'}
    </GlassButton>
  );
}

const styles = StyleSheet.create({
  compactGlyph: {
    color: colors.accent,
    fontSize: 18,
    lineHeight: 22,
    fontWeight: '600',
  },
  compactBusy: {
    minWidth: 18,
    minHeight: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.xs,
  },
});
